import React, { FC, useCallback, useMemo, memo } from 'react';
import { useLazyQuery } from '@apollo/client';
import _map from 'lodash/map';
import FormikSelectableSearchDropdown from '@minecraft.formik-selectable-search-dropdown';
import ErrorMessage from '@minecraft.error-message';
import { useTranslation } from '@blocs.i18n';
import { QUERY_DMAS } from '../../FilterBasic/FilterBasicCollection/FilterBasicCollectionGraphQL';
import { FilterData } from '../../shared/types';
import { getLocations } from '../../shared/utils';
import { Label, StyledFormikSelectableSearchDropdown } from '../../shared/styles';

type Props = FilterData;

const WorkingLocations: FC<Props> = ({ name, label }) => {
  const { t } = useTranslation();

  const [getDmas, { data, error, loading }] = useLazyQuery(QUERY_DMAS);

  const { usLocations, caLocations, euLocations, auLocations, additionalLocations, laLocations } = useMemo(
    () =>
      getLocations({
        dmas: data?.dmas || [],
      }),
    [data?.dmas]
  );

  const handleDmasFetch = useCallback((): void => {
    if (!data) {
      getDmas();
    }
  }, [data]);

  const renderLocationList = useCallback(({ id: regionId, name: regionName, cities }): JSX.Element => {
    return (
      <FormikSelectableSearchDropdown.Section isToggleable={false} key={regionId} name={regionName}>
        {_map(cities, ({ id: cityId, name: cityName }) => (
          <FormikSelectableSearchDropdown.Option key={cityId} value={cityId} label={cityName} />
        ))}
      </FormikSelectableSearchDropdown.Section>
    );
  }, []);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <>
      <Label>{label}</Label>
      <StyledFormikSelectableSearchDropdown
        saveAsOption
        name={name}
        isLoading={loading}
        onOptionsFetch={handleDmasFetch}
      >
        {usLocations?.length > 0 ? (
          <FormikSelectableSearchDropdown.Section isBold name={t('common:label.country.unitedStates')}>
            {_map(usLocations, renderLocationList)}
          </FormikSelectableSearchDropdown.Section>
        ) : (
          <></>
        )}
        {caLocations?.[0]?.cities?.length > 0 ? (
          <FormikSelectableSearchDropdown.Section isBold name={t('common:label.country.canada')}>
            {_map(caLocations?.[0].cities, ({ id: cityId, name: cityName }) => (
              <FormikSelectableSearchDropdown.Option key={cityId} value={cityId} label={cityName} />
            ))}
          </FormikSelectableSearchDropdown.Section>
        ) : (
          <></>
        )}
        {euLocations?.length > 0 ? (
          <FormikSelectableSearchDropdown.Section isBold name={t('common:label.country.unitedKingdomIreland')}>
            {_map(euLocations, renderLocationList)}
          </FormikSelectableSearchDropdown.Section>
        ) : (
          <></>
        )}
        {auLocations?.length > 0 ? (
          <FormikSelectableSearchDropdown.Section isBold name={t('common:label.country.australiaNewZealand')}>
            {_map(auLocations, renderLocationList)}
          </FormikSelectableSearchDropdown.Section>
        ) : (
          <></>
        )}
        {laLocations?.length > 0 ? (
          <FormikSelectableSearchDropdown.Section isBold name={t('common:label.country.latinAmerica')}>
            {_map(laLocations, renderLocationList)}
          </FormikSelectableSearchDropdown.Section>
        ) : (
          <></>
        )}
        {additionalLocations?.length > 0 ? (
          <FormikSelectableSearchDropdown.Section isBold name={t('common:label.addtionalLocations')}>
            {_map(additionalLocations, renderLocationList)}
          </FormikSelectableSearchDropdown.Section>
        ) : (
          <></>
        )}
      </StyledFormikSelectableSearchDropdown>
    </>
  );
};

export default memo(WorkingLocations);
